import React, { useEffect, useRef } from "react";
import { HStack } from "@chakra-ui/react";
import { Geocoder } from "cesium";
import { useAppStore } from "../../../context/store";

function MapGeocoder() {
  const viewerRef = useAppStore.getState().viewerRef;
  const geocoderRef = useRef(null);
  const geocoderInstanceRef = useRef(null);
  const cacheRef = useRef([]);
  const listenersCleanupRef = useRef(null);

  if (cacheRef.current.length === 0) {
    const saved = localStorage.getItem("geocoderCache");
    try {
      cacheRef.current = saved ? JSON.parse(saved) : [];
    } catch {
      cacheRef.current = [];
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const viewer = viewerRef.current;
      const container = geocoderRef.current;

      if (geocoderInstanceRef.current) {
        clearInterval(interval);
        return;
      }

      if (!viewer || !container) return;

      let geocoder;
      try {
        geocoder = new Geocoder({
          container,
          scene: viewer.scene,
        });
      } catch (e) {
        clearInterval(interval);
        return;
      }

      geocoderInstanceRef.current = geocoder;

      const viewModel = geocoder && geocoder.viewModel;
      const input = container.querySelector("input");
      const resultsList = container.querySelector(".search-results");
      const dataListId = "geocoder-history";

      let dataList = container.querySelector(`#${dataListId}`);
      if (!dataList) {
        dataList = document.createElement("datalist");
        dataList.id = dataListId;
        container.appendChild(dataList);
      }

      if (input) {
        input.setAttribute("list", dataListId);
      }

      const updateDataList = () => {
        if (!dataList) return;
        dataList.innerHTML = "";
        cacheRef.current.forEach((value) => {
          const option = document.createElement("option");
          option.value = value;
          dataList.appendChild(option);
        });
      };

      const handleResultClick = (event) => {
        const li = event.target.closest("li");
        if (!li) return;
        const text = li.textContent?.trim();
        if (!text) return;

        const current = cacheRef.current;
        const without = current.filter((v) => v !== text);
        without.unshift(text);
        cacheRef.current = without.slice(0, 10);

        // save cache
        localStorage.setItem("geocoderCache", JSON.stringify(cacheRef.current));

        updateDataList();
      };

      const handleInputFocus = () => {
        updateDataList();
      };

      const handleInputChange = () => {
        if (!viewModel || !input) return;

        const value = input.value.trim();
        if (!value) return;

        if (!cacheRef.current.includes(value)) return;

        viewModel.searchText = value;

        const searchFn = viewModel.search;
        if (typeof searchFn === "function") {
          try {
            searchFn();
          } catch (e) {}
        }
      };

      if (resultsList) {
        resultsList.addEventListener("click", handleResultClick);
      }
      if (input) {
        input.addEventListener("focus", handleInputFocus);
        input.addEventListener("change", handleInputChange);
      }

      listenersCleanupRef.current = () => {
        if (resultsList) {
          resultsList.removeEventListener("click", handleResultClick);
        }
        if (input) {
          input.removeEventListener("focus", handleInputFocus);
          input.removeEventListener("change", handleInputChange);
        }
      };

      clearInterval(interval);
    }, 100);

    return () => {
      clearInterval(interval);

      if (listenersCleanupRef.current) {
        try {
          listenersCleanupRef.current();
        } catch {}
        listenersCleanupRef.current = null;
      }

      if (geocoderInstanceRef.current) {
        try {
          geocoderInstanceRef.current.destroy();
        } catch {}
        geocoderInstanceRef.current = null;
      }
    };
  }, [viewerRef]);

  return <HStack mr={10} ref={geocoderRef} zIndex={1001} />;
}

export default MapGeocoder;
